import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {ChevronDown} from 'lucide-react';
import { typography, textColors } from "../../styles/typography";
import { trackFAQInteraction } from "../../utils/trackingHelpers";

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "¿Qué es exactamente la Máquina de Ventas Zoefel IA?",
    answer: "Es un sistema completo 'llave en mano' que captura, nutre y califica prospectos automáticamente por WhatsApp 24/7. Incluye CRM, pipeline de ventas, automatización omnicanal, y Agentes de IA que trabajan como tu equipo de ventas mientras tú cierras negocios. No es solo software, es una máquina de ventas implementada y gestionada por nosotros."
  },
  {
    question: "¿Cuánto tiempo toma la implementación?",
    answer: "72 horas o menos. Una vez que activas tu sistema, nuestro equipo configura todo personalizado para tu negocio. Tú solo necesitas proporcionar la información de tu empresa y acceso a tus cuentas de WhatsApp/Email. En el Plan Enterprise la implementación es prioritaria en 48 horas."
  },
  {
    question: "¿Necesito conocimientos técnicos para usar la Máquina de Ventas?",
    answer: "Absolutamente no. Si sabes usar WhatsApp y correo electrónico, puedes usar tu Máquina de Ventas. El sistema está diseñado para ser intuitivo y lo implementamos nosotros. Además, te proporcionamos capacitación completa, soporte continuo y acompañamiento hasta que domines el sistema."
  },
  {
    question: "¿Qué pasa si no veo resultados?",
    answer: "Tenemos una garantía \"Éxito Asegurado\" de 30 días. Si después de usar la Máquina de Ventas correctamente no ves un aumento en leads calificados o citas agendadas, trabajaremos contigo sin costo adicional hasta que lo logres. Tu éxito es nuestro éxito."
  },
  {
    question: "¿Cuál es la diferencia entre el Plan Starter y Enterprise?",
    answer: "Plan Starter ($34/mes) incluye la plataforma completa y la Máquina de Ventas, pero los Agentes IA tienen costo adicional ($20 USD/agente/mes). Plan Enterprise ($170/mes con 50% OFF - precio regular $340/mes) incluye TODO ILIMITADO: 4 Agentes IA con uso ilimitado mensual, y en el plan anual puedes crear TODOS los Agentes IA que necesites. Te ahorras hasta $4,213 USD/año vs stack fragmentado."
  },
  {
    question: "¿Los Agentes IA realmente funcionan 24/7 sin descanso?",
    answer: "Sí, exactamente. Los Agentes IA están disponibles las 24 horas del día, los 7 días de la semana, sin vacaciones, sin días festivos, sin descansos. Responden al instante, califican leads según tus criterios exactos, nutren prospectos con tus mensajes personalizados, y agendan citas directamente en tu calendario mientras tú duermes."
  },
  {
    question: "¿Puedo cancelar mi suscripción cuando quiera?",
    answer: "Sí, sin permanencia forzada. Puedes cancelar tu suscripción mensual en cualquier momento. Sin embargo, la inversión de implementación inicial no es reembolsable después de completada la configuración porque ya dedicamos tiempo y recursos a personalizar tu sistema."
  },
  {
    question: "¿Qué son los 'consumos variables' y cuánto cuestan?",
    answer: "Los consumos variables incluyen: envío de SMS, mensajes de WhatsApp, correos electrónicos, y minutos de telefonía (si aplica). Estos se cobran según uso real. En Plan Starter también se cobran los Agentes IA por separado. En Plan Enterprise Mensual, los 4 Agentes IA están incluidos con uso ILIMITADO sin cargo adicional."
  },
  {
    question: "¿Funciona para mi industria o tipo de negocio?",
    answer: "La Máquina de Ventas es adaptable a múltiples industrias: coaches, consultores, agencias, servicios profesionales, ecommerce, bienes raíces, educación, salud/wellness, servicios B2B, y más. Durante la implementación personalizamos todo según tu industria, lenguaje, y modelo de negocio específico. Si vendes servicios de alto valor, esto funciona para ti."
  },
  {
    question: "¿Qué tipo de soporte técnico incluyen?",
    answer: "Incluimos soporte técnico prioritario por email y chat en español. Respondemos en menos de 24 horas hábiles. Además, tienes acceso a nuestra base de conocimientos con tutoriales en video, documentación detallada, y capacitación personalizada durante la implementación."
  }
];

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    const newIndex = openIndex === index ? null : index;
    setOpenIndex(newIndex);
    
    // Track FAQ interaction when opening a question
    if (newIndex !== null) {
      trackFAQInteraction(faqData[index].question, index);
    }
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <span className={`${typography.badge} bg-indigo-500/20 text-indigo-600 rounded-full px-4 py-2 mb-6`}>
            PREGUNTAS FRECUENTES
          </span>
          <h2 className={`${typography.h2} mb-6`}>
            ¿Tienes dudas? Las respondemos aquí
          </h2>
          <p className={`${typography.bodyLarge} ${textColors.tertiary}`}>
            Todo lo que necesitas saber antes de activar tu sistema
          </p>
        </div>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-50 rounded-xl overflow-hidden border-2 border-[#00C2FF]/30 hover:border-[#00C2FF]/50 shadow-lg hover:shadow-[#00C2FF]/20 transition-all duration-300"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
              >
                <span className={`${typography.h6} pr-8`}>
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <ChevronDown className="w-6 h-6 text-indigo-600" />
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className={`px-8 pb-6 ${typography.bodyBase} ${textColors.secondary} leading-relaxed`}>
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
          <h3 className={`${typography.h3} mb-4`}>
            ¿No encuentras tu respuesta?
          </h3>
          <p className={`${typography.bodyBase} ${textColors.secondary} mb-6`}>
            Nuestro equipo está listo para ayudarte con cualquier duda específica
          </p>
          <a
            href="#contacto"
            className={`inline-block ${typography.ctaBase} bg-indigo-600 ${textColors.primaryLight} py-3 px-8 rounded-lg hover:bg-indigo-700 transition-colors`}
          >
            Habla con Nosotros
          </a>
        </div>
      </div>
    </section>
  );
};
